import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SaveStatus } from '@design/models/save-status.model';
import { fromAdditionalToolsActions } from '@design/store/additional-tools';
import { DesignState } from '@design/store/design.reducer';
import { fromSimulationActions } from '@design/store/simulation';
import { Store } from '@ngrx/store';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/helpers/auth.service';
import { environment } from 'src/environments/environment';
import { getHideLoaderHeaders } from 'src/app/helpers/interceptors/loader.interceptor';
export interface PreferencesParams {
    account_id: string;
    email_id: string;
}

/** Must match CommonConstants.KEY_QUOTE_GROUND_MAP_IMAGE on the backend. */
const GROUND_MAP_IMAGE_TYPE = 'quote_ground_map_image';

@Injectable({ providedIn: 'root' })
export class AdditionalToolsApi {

    private readonly endpoint = 'quotes/custom-production-upload-v1';

    private save3dImageActiveCalls: Map<string, Subject<void>> = new Map();
    private saveQuoteBackgroundImage$?: Subject<void>;
    private sendImageUpdateEvent$ = new Subject<void>();
    
    constructor(private http: HttpClient,private authService: AuthService, private store: Store<DesignState>) {}

    read(readParams: any): Observable<any> {
        return this.http.post(`${environment.newApiUrl}` + this.endpoint, readParams.uploadDetailsReadParams).pipe(
            map((data) => data),
            catchError((error) => throwError(error))
        );
    }

    // Reads back the custom generation uploaded against this quote revision.
    getSavedCustomGeneration(): Observable<any> {
        let readParams = new HttpParams();
        readParams = readParams.append('account_id', this.authService.getItem('account_id'));
        readParams = readParams.append('isEditQuote', 'true');
        readParams = readParams.append('quote_id', this.authService.getItem('quote_id', 'localStorage'));
        readParams = readParams.append('rev_no', this.authService.getItem('revision_number', 'localStorage'));

        return this.http.get(`${environment.newApiUrl}quotes/custom-generation-v1?${readParams}`, { headers: getHideLoaderHeaders() }).pipe(
            map((data) => data),
            catchError((error) => throwError(error))
        );
    }

    getCPFRates(reqBody: any): Observable<any> {
        return this.http.post(`${environment.newApiUrl}lead/bill/utilityinfo`, reqBody.reqBody);
    }

    getMonitoringSystems(readParams: PreferencesParams): Observable<any> {
        const params: any = {
            account_id: this.authService.getItem('account_id'),
            email_id: this.authService.getItem('email_id')
        };
        return this.http.get(`${environment.newApiUrl}hardware/monitoring/selected`, { params });
    }

    savepostPvRate(meterAllocation:any): Observable<any>{
        console.log("save energy data" +meterAllocation);
        
        return this.http.post(`${environment.newApiUrl}lead/post-pv-rate`, meterAllocation);
          
    }

    // updateMeter(meterObj:any): Observable<any>{
    //     return this.http.post(`${environment.newApiUrl}lead/post-pv-rate`, meterObj);
    // }

    saveQuoteBackgroundImage(readParams: any): Observable<any> {
        /// cancel previous api call
        this.saveQuoteBackgroundImage$?.next();
        this.saveQuoteBackgroundImage$?.complete();
        this.saveQuoteBackgroundImage$ = new Subject<void>();

        const params: any = {
            quote_id: this.authService.getItem('quote_id', 'localStorage'),
            revision_number: Number(this.authService.getItem('revision_number')),
            qrn: this.authService.getItem('qrn', 'localStorage'),
            crn: this.authService.getItem('crn', 'localStorage'),
            backgroundImage: readParams.uploadDetailsReadParams.backgroundImage,
            backgroundBound: {},
            account_id: this.authService.getItem('account_id'),
            type:readParams.uploadDetailsReadParams.type
        };

        const options = {
            headers: new HttpHeaders({
                'x-aws-enact-key': '5A5C7D4736176IOP497347SUAY72656174XTZ',
            }),
        };

        return this.http.put(`${environment.apiUrl}nact-backend/quotes/saveBackgroundImage`, params, options).pipe(
            takeUntil(this.saveQuoteBackgroundImage$),
            map(()=> {
                this.store.dispatch(fromAdditionalToolsActions.setBackgroundImageAPIStatus({ backgroundImageAPIStatus: SaveStatus.SAVED }));
            }),
            catchError((error) => throwError(error))
        );
    }

    getQuoteBackgroundImage(params: any): Observable<any> {
        let readParams = new HttpParams();

        if (this.authService.getItem('account_id')) {
            readParams = readParams.append('account_id', this.authService.getItem('account_id'));
        }

        readParams = readParams.append('backgroundImageId', params.uploadDetailsReadParams.backgroundImageId);

        if (this.authService.getItem('quote_id')) {
            readParams = readParams.append('quote_id', this.authService.getItem('quote_id', 'localStorage'));
        }

        if (this.authService.getItem('crn')) {
            readParams = readParams.append('crn', this.authService.getItem('crn', 'localStorage'));
        }

        if (this.authService.getItem('revision_number')) {
            readParams = readParams.append('revision_number', this.authService.getItem('revision_number', 'localStorage'));
        }

        if (params.uploadDetailsReadParams.type) {
            readParams = readParams.append('type', params.uploadDetailsReadParams.type);
        }

        return this.http.get(`${environment.apiUrl}nact-backend/quotes/getBackgroundImage?${readParams}`,).pipe(
            map((data) => data),
            catchError((error) => throwError(error))
        );
    }

    /**
     * Persists the current ground-map raster as an immutable, per-revision pin so
     * the saved design never drifts when Google refreshes its underlying imagery.
     * Intentionally does NOT flip backgroundImageAPIStatus (that status gates
     * navigation/report flows and is unrelated to the map texture).
     * Non-blocking: callers should swallow failures since the live image is already shown.
     */
    savePinnedGroundMap(base64Image: string, mapType: string): Observable<any> {
        const params: any = {
            quote_id: this.authService.getItem('quote_id', 'localStorage'),
            revision_number: Number(this.authService.getItem('revision_number')),
            qrn: this.authService.getItem('qrn', 'localStorage'),
            crn: this.authService.getItem('crn', 'localStorage'),
            backgroundImage: base64Image,
            backgroundBound: {},
            account_id: this.authService.getItem('account_id'),
            type: GROUND_MAP_IMAGE_TYPE,
            // Pins are keyed per map type so switching Google Map/Solar/NearMap
            // does not serve a stale raster.
            map_type: mapType || '',
        };

        const options = {
            headers: getHideLoaderHeaders().set('x-aws-enact-key', '5A5C7D4736176IOP497347SUAY72656174XTZ'),
        };

        return this.http.put(`${environment.apiUrl}nact-backend/quotes/saveBackgroundImage`, params, options).pipe(
            map(() => true),
            catchError((error) => throwError(error))
        );
    }

    /**
     * Reads the pinned ground-map raster for the current quote revision.
     * Backend returns { image: '' } when nothing is pinned yet (legacy designs /
     * freshly-created revisions), which callers treat as "no pin".
     */
    getPinnedGroundMap(mapType: string): Observable<any> {
        let readParams = new HttpParams();

        if (this.authService.getItem('account_id')) {
            readParams = readParams.append('account_id', this.authService.getItem('account_id'));
        }
        if (this.authService.getItem('quote_id')) {
            readParams = readParams.append('quote_id', this.authService.getItem('quote_id', 'localStorage'));
        }
        if (this.authService.getItem('crn')) {
            readParams = readParams.append('crn', this.authService.getItem('crn', 'localStorage'));
        }
        if (this.authService.getItem('revision_number')) {
            readParams = readParams.append('revision_number', this.authService.getItem('revision_number', 'localStorage'));
        }
        readParams = readParams.append('type', GROUND_MAP_IMAGE_TYPE);
        readParams = readParams.append('map_type', mapType || '');

        return this.http.get(`${environment.apiUrl}nact-backend/quotes/getBackgroundImage?${readParams}`, { headers: getHideLoaderHeaders() }).pipe(
            map((data) => data),
            catchError((error) => throwError(error))
        );
    }

    saveQuote3DIsometricViewImage(readParams: any): Observable<any> {
        const params: any = {
            quote_id: this.authService.getItem('quote_id', 'localStorage'),
            revision_number: Number(this.authService.getItem('revision_number')),
            qrn: this.authService.getItem('qrn', 'localStorage'),
            image_string: readParams.uploadDetailsReadParams.backgroundImage,
            image_type: readParams.uploadDetailsReadParams.imageType,
            account_id: this.authService.getItem('account_id'),
        };

        /// cancel previous api call based on image type
        const imageType$ = this.save3dImageActiveCalls.get(params.image_type);
        imageType$?.next();
        imageType$?.complete();
        this.save3dImageActiveCalls.set(params.image_type, new Subject<void>());

        /// Removed broker api to avoid network delay. 
        /// Reduced network time by 3 seconds.
        return this.http.post(`${environment.apiUrl}nact-backend/quotes/saveHouseViewImage`, params).pipe(
            takeUntil(this.save3dImageActiveCalls.get(params.image_type)!),
            map(()=> {
                this.store.dispatch(fromAdditionalToolsActions.setHouseViewImageAPIStatus({ houseViewImageAPIStatus: SaveStatus.SAVED }));
            }),
            catchError((error) => throwError(error)) 
        );
    }

    sendSiteImageUpdateEvent(){
        this.sendImageUpdateEvent$.next();
    }

    getNewImage(): Observable<void>{
        return this.sendImageUpdateEvent$.asObservable();
    }
}